import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { useTheme } from '../../contexts/ThemeContext';
import { usePro } from '../../contexts/ProContext';
import { Mic, Check, ArrowRight, Sparkles } from 'lucide-react';

// Animated shimmer title component
const ShimmerTitle = ({ children, gradientColors, className = '' }) => {
  return (
    <span
      className={`relative inline-block ${className}`}
      style={{
        background: `linear-gradient(
          90deg,
          ${gradientColors[0]} 0%,
          ${gradientColors[1]} 25%,
          ${gradientColors[2]} 50%,
          ${gradientColors[1]} 75%,
          ${gradientColors[0]} 100%
        )`,
        backgroundSize: '200% 100%',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        animation: 'shimmer 3s ease-in-out infinite',
      }}
    >
      {children}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
      `}</style>
    </span>
  );
};

/**
 * MagicCoachHeroCard - Compact feature showcase for Tajweed Coach AI
 * 
 * Design: Theme-matched, compact card
 * - Royal Knight: Premium elegant with animated gold shimmer title
 * - Kids Paradise: Friendly with playful styling
 */
const MagicCoachHeroCard = () => {
  const { appTheme, mode } = useTheme();
  const { isPro, requestFeatureAccess } = usePro();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const isDark = mode === 'dark';
  const isKids = appTheme === 'kids';
  const isRoyal = appTheme === 'royal';
  
  // Handle Try Now click - check Pro status first
  const handleTryNowClick = () => {
    if (isPro) {
      // Pro users go directly to Quran page with tajweed mode
      navigate('/quran?tajweed=true');
    } else {
      // Non-Pro users see the upgrade modal
      requestFeatureAccess({
        id: 'tajweed_coach',
        name: t('tajweedCoach.title', 'Tajweed Coach AI'),
        description: t('tajweedCoach.subtitle', 'Your personal Quran recitation coach. Receive intelligent, real-time feedback while you recite.')
      }, () => {
        // After access granted (via subscription or ad), navigate to Quran
        navigate('/quran?tajweed=true');
      });
    }
  };
  
  // Theme-specific styling
  const getThemeStyles = () => {
    switch (appTheme) {
      case 'emerald':
        return {
          cardBg: isDark ? '#0f2920' : '#FFFBEB',
          cardBorder: isDark ? '#10b981' : '#d4a853',
          iconBg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          iconShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
          title: isDark ? '#ffffff' : '#000000', // BLACK for light mode
          subtitle: isDark ? '#a7f3d0' : '#A16207',
          body: isDark ? '#a7f3d0' : '#4a6d5e',
          checkColor: isDark ? '#10b981' : '#059669',
          ctaBg: isDark ? '#10b981' : '#059669',
          ctaText: '#ffffff',
        };
      case 'royal':
        // Premium Royal - BLACK title, gold accents, elegant styling
        return {
          cardBg: isDark ? '#1e1b4b' : '#fefce8',
          cardBorder: '#c9a227',
          iconBg: 'linear-gradient(135deg, #c9a227 0%, #b8860b 100%)',
          iconShadow: '0 4px 16px rgba(201, 162, 39, 0.5)',
          title: isDark ? '#fef3c7' : '#000000', // BLACK for light mode
          subtitle: '#c9a227', // Gold
          body: isDark ? 'rgba(254, 243, 199, 0.8)' : '#44403c',
          checkColor: '#c9a227',
          ctaBg: 'linear-gradient(135deg, #c9a227 0%, #b8860b 100%)',
          ctaText: '#1e1b4b',
          accentGold: '#c9a227',
        };
      case 'kids':
        return {
          cardBg: isDark ? '#3730a3' : '#FCE7F3',
          cardBorder: isDark ? '#818cf8' : '#DB2777',
          iconBg: isDark 
            ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' 
            : 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
          iconShadow: isDark 
            ? '0 4px 12px rgba(251, 191, 36, 0.5)' 
            : '0 4px 12px rgba(236, 72, 153, 0.4)',
          title: isDark ? '#ffffff' : '#000000', // BLACK for light mode
          subtitle: isDark ? '#fbbf24' : '#DB2777',
          body: isDark ? '#c4b5fd' : '#9333ea',
          checkColor: isDark ? '#fbbf24' : '#DB2777',
          ctaBg: isDark 
            ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' 
            : 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
          ctaText: isDark ? '#1e1b4b' : '#ffffff',
        };
      default: // classic
        return {
          cardBg: isDark ? '#1e293b' : '#ffffff',
          cardBorder: isDark ? '#334155' : '#e2e8f0',
          iconBg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          iconShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
          title: isDark ? '#ffffff' : '#000000', // BLACK for light mode
          subtitle: isDark ? '#34d399' : '#10b981',
          body: isDark ? '#94a3b8' : '#475569',
          checkColor: isDark ? '#34d399' : '#10b981',
          ctaBg: '#EAB308', // Gold — matches Classic theme secondary accent
          ctaText: '#1E293B', // Dark navy for readable contrast on gold
        };
    }
  };
  
  const styles = getThemeStyles();
  
  const benefits = isKids 
    ? [t('magicCoach.benefits.funPractice', 'Fun practice!'), t('magicCoach.benefits.trackProgress', 'Track progress'), t('magicCoach.benefits.learnTogether', 'Learn together')]
    : [t('magicCoach.benefits.realTimeGuidance', 'Real-time guidance'), t('magicCoach.benefits.wordByWord', 'Word-by-word tracking'), t('magicCoach.benefits.focusedLearning', 'Focused learning')];

  // Royal Knight: Premium elegant card with gold accents
  if (isRoyal) {
    // For Royal theme - use high contrast colors that work on both modes
    // The Royal theme homepage tends to use dark backgrounds
    const isRoyalDark = isDark || true; // Royal theme typically appears dark
    const royalTitle = '#fef3c7'; // Warm cream - high contrast on dark bg
    const royalSubtitle = '#c9a227'; // Gold
    const royalBody = 'rgba(254, 243, 199, 0.85)';
    const frameGreen = '#10b981'; // Emerald green for frame
    
    return (
      <div 
        className="relative overflow-hidden rounded-xl transition-all duration-300"
        style={{ 
          background: 'linear-gradient(135deg, #2a2455 0%, #1e1b4b 100%)',
          border: `2px solid ${frameGreen}`,
          boxShadow: '0 4px 24px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(16, 185, 129, 0.1)',
        }}
        data-testid="magic-coach-hero-card"
      >
        {/* Elegant green line accent at top */}
        <div 
          className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-0.5"
          style={{ background: `linear-gradient(90deg, transparent, ${frameGreen}, transparent)` }}
        />
        
        <div className="px-6 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6">
            
            {/* Premium Gold Icon */}
            <div 
              className="rounded-xl p-3.5 mb-4 sm:mb-0 self-center sm:self-auto flex-shrink-0"
              style={{ 
                background: 'linear-gradient(135deg, #c9a227 0%, #b8860b 100%)', 
                boxShadow: '0 4px 16px rgba(201, 162, 39, 0.5)',
              }}
            >
              <Mic className="h-7 w-7 text-white" strokeWidth={1.5} />
            </div>
            
            {/* Content */}
            <div className="flex-1 text-center sm:text-left">
              <h3 className="font-bold text-xl tracking-tight mb-1">
                <ShimmerTitle 
                  gradientColors={['#fef3c7', '#fbbf24', '#fef3c7']}
                  className="font-bold"
                >
                  {t('tajweedCoach.title', 'Tajweed Coach AI')}
                </ShimmerTitle>
              </h3>
              <p 
                className="text-sm font-medium mb-3"
                style={{ color: royalSubtitle }}
              >
                {t('magicCoach.personalCoach', 'Your personal Quran recitation coach')}
              </p>
              
              {/* Benefits with gold dots */}
              <div className="flex flex-wrap justify-center sm:justify-start gap-x-5 gap-y-1">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div 
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: '#c9a227' }}
                    />
                    <span 
                      className="text-xs font-medium"
                      style={{ color: royalBody }}
                    >
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Premium Gold CTA */}
            <div className="mt-4 sm:mt-0 flex justify-center sm:justify-end">
              <Button 
                onClick={handleTryNowClick}
                data-testid="magic-coach-cta"
                className="group px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 hover:scale-105"
                style={{ 
                  background: 'linear-gradient(135deg, #c9a227 0%, #b8860b 100%)',
                  color: '#1e1b4b',
                  boxShadow: '0 2px 12px rgba(201, 162, 39, 0.4)',
                }}
              >
                {t('magicCoach.tryNow', 'Try Now')}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
            
          </div>
        </div>
        
        {/* Elegant green line accent at bottom */}
        <div 
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-0.5"
          style={{ background: `linear-gradient(90deg, transparent, ${frameGreen}, transparent)` }}
        />
      </div>
    );
  }

  // Kids Paradise: Extra friendly styling
  if (isKids) {
    return (
      <div 
        className="relative overflow-hidden rounded-2xl transition-all duration-300"
        style={{ 
          backgroundColor: styles.cardBg,
          border: `3px solid ${styles.cardBorder}`,
          boxShadow: isDark 
            ? '0 4px 20px rgba(129, 140, 248, 0.2)' 
            : '0 4px 20px rgba(219, 39, 119, 0.15)',
        }}
        data-testid="magic-coach-hero-card"
      >
        {/* Playful decorative elements */}
        <div className="absolute top-2 right-3 text-2xl opacity-60">✨</div>
        <div className="absolute bottom-2 left-3 text-xl opacity-50">🌟</div>
        
        <div className="px-5 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-5">
            
            {/* Friendly Icon */}
            <div 
              className="rounded-2xl p-4 mb-4 sm:mb-0 self-center sm:self-auto flex-shrink-0 transform hover:scale-110 transition-transform duration-300"
              style={{ background: styles.iconBg, boxShadow: styles.iconShadow }}
            >
              <Mic className="h-8 w-8 text-white" strokeWidth={1.5} />
            </div>
            
            {/* Content */}
            <div className="flex-1 text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                <h3 className="font-bold text-xl">
                  <ShimmerTitle 
                    gradientColors={isDark ? ['#fbbf24', '#f472b6', '#fbbf24'] : ['#ec4899', '#8b5cf6', '#ec4899']}
                    className="font-bold"
                  >
                    {t('kidsTajweed.title', 'Quran Practice')}
                  </ShimmerTitle>
                </h3>
                <Sparkles className="h-5 w-5" style={{ color: styles.subtitle }} />
              </div>
              <p 
                className="text-sm font-medium mb-3"
                style={{ color: styles.subtitle }}
              >
                {t('magicCoach.kidsSubtitle', 'Learn to read Quran beautifully!')}
              </p>
              
              {/* Fun benefits */}
              <div className="flex flex-wrap justify-center sm:justify-start gap-3">
                {benefits.map((benefit, index) => (
                  <div 
                    key={index} 
                    className="flex items-center gap-1.5 px-2 py-1 rounded-full"
                    style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(219, 39, 119, 0.1)' }}
                  >
                    <Check 
                      className="h-3.5 w-3.5 flex-shrink-0" 
                      style={{ color: styles.checkColor }}
                      strokeWidth={3} 
                    />
                    <span 
                      className="text-xs font-semibold"
                      style={{ color: styles.body }}
                    >
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Friendly CTA */}
            <div className="mt-4 sm:mt-0 flex justify-center sm:justify-end">
              <Button 
                onClick={handleTryNowClick}
                data-testid="magic-coach-cta"
                className="group px-6 py-3 rounded-xl text-sm font-bold transition-all duration-300 hover:scale-105 shadow-lg"
                style={{ 
                  background: styles.ctaBg,
                  color: styles.ctaText,
                  boxShadow: styles.iconShadow,
                }}
              >
                Let&apos;s Go! 
                <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
            
          </div>
        </div>
      </div>
    );
  }

  // Standard themes (Classic, Emerald)
  return (
    <div 
      className="relative overflow-hidden rounded-xl transition-colors duration-300"
      style={{ 
        backgroundColor: styles.cardBg,
        border: `1px solid ${styles.cardBorder}`,
      }}
      data-testid="magic-coach-hero-card"
    >
      <div className="px-4 py-5 sm:px-6 sm:py-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6">
          
          {/* Icon */}
          <div 
            className="rounded-xl p-3 mb-4 sm:mb-0 self-center sm:self-auto flex-shrink-0"
            style={{ background: styles.iconBg, boxShadow: styles.iconShadow }}
          >
            <Mic className="h-7 w-7 text-white" strokeWidth={1.5} />
          </div>
          
          {/* Content */}
          <div className="flex-1 text-center sm:text-left">
            <h3 className="font-bold text-lg mb-1">
              <ShimmerTitle 
                gradientColors={
                  isDark 
                    ? ['#34d399', '#10b981', '#34d399'] 
                    : ['#059669', '#10b981', '#059669']
                }
                className="font-bold"
              >
                {t('tajweedCoach.title', 'Tajweed Coach AI')}
              </ShimmerTitle>
            </h3>
            <p 
              className="text-sm mb-2"
              style={{ color: styles.subtitle }}
            >
              {t('magicCoach.personalCoach', 'Your personal Quran recitation coach')}
            </p>
            
            {/* Benefits */}
            <div className="flex flex-wrap justify-center sm:justify-start gap-x-4 gap-y-1">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-1.5">
                  <Check 
                    className="h-3.5 w-3.5 flex-shrink-0" 
                    style={{ color: styles.checkColor }}
                    strokeWidth={2.5} 
                  />
                  <span 
                    className="text-xs"
                    style={{ color: styles.body }}
                  >
                    {benefit}
                  </span>
                </div>
              ))}
            </div>
          </div>
          
          {/* CTA */}
          <div className="mt-4 sm:mt-0 flex justify-center sm:justify-end">
            <Button 
              onClick={handleTryNowClick}
              data-testid="magic-coach-cta"
              className="group px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105"
              style={{ 
                backgroundColor: styles.ctaBg,
                color: styles.ctaText,
              }}
            >
              {t('magicCoach.tryNow', 'Try Now')}
              <ArrowRight className="ml-1.5 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default MagicCoachHeroCard;
